import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormCheckbox from "../../components/form/FormCheckbox";
import FormColorDropdown from "../../components/form/FormColorDropdown";
import FormDropdown from "../../components/form/FormDropdown";
import FormEnableDropdown from "../../components/form/FormEnableDropdown";
import FormTextField from "../../components/form/FormTextField";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorGroup from "../../components/inspector/InspectorGroup";
import InspectorHeader from "../../components/inspector/InspectorHeader";
import InspectorRow from "../../components/inspector/InspectorRow";
import Icon from "../../components/interface/Icon";
import Identifier from "../../components/interface/Identifier";
import IdentifierSlug from "../../components/interface/IdentifierSlug";
import { withScreenContext } from "../../components/screen/ScreenContext";
import ToolbarTextButton from "../../components/toolbar/ToolbarTextButton";
import swatchColorOptions from "../../constants/colorOptions";
import { INSPECTOR_MULTIPLE_ITEM_SELECTED } from "../../constants/inspectorMessages";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import PubSubTopics from "../../constants/pubSubTopics";
import screenModes from "../../constants/screenModes";
import dialogs from "../../dialogs";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { referenceSelectors } from "../../modules/definition/references";
import definitionSelectors from "../../modules/definition/selectors";
import { timelineViewSettingsSelectors } from "../../modules/definition/timelineViewSettings";
import { typeSelectors } from "../../modules/definition/types";
import { itemOperations, itemSelectors } from "../../modules/items";
import { relationshipSelectors } from "../../modules/relationships";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { PropTypeShapes } from "../../types";
import { chooseValueForIos, mergeDispatchToProps } from "../../utils/codeSplit";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { trackEvent } from "../../utils/trackAnalytics";
import selectors from "./selectors";

const onIdentifierClick = itemId => {
  PubSub.publish(PubSubTopics.scrollToItem, { id: itemId });
};

const getOnTypeChange = useProps => newTypeId => {
  const { items, itemsById, typesById, referencesById, itemIds, onTypeChange } = useProps;
  const shouldShowConfirm = typeSelectors.doesNewTypeBreakItemValue(
    newTypeId,
    items,
    itemsById,
    typesById,
    referencesById
  );

  const apply = () => onTypeChange(itemIds.toArray(), newTypeId);

  if (shouldShowConfirm) {
    dialogs.okCancelPrompt(interfaceMessages.TYPE_CHANGE_CONFIRMATION, result => {
      if (result) {
        apply();
      }
    });
  } else {
    apply();
  }
};

const getCollapseIcon = (onCollapseClick, showCollapseLeft, text = "Back") =>
  onCollapseClick ? (
    showCollapseLeft ? (
      <ToolbarTextButton icon="leftArrow" text={text} navigateBackStyle={true} onClick={onCollapseClick} />
    ) : (
      <Icon
        tooltip="Hide Inspector Panel"
        icon="rightArrow"
        className="inspectorCollapseArrow"
        color="iconMono"
        onClick={onCollapseClick}
      />
    )
  ) : (
    <div className="spacer" style={{ flex: 1 }} />
  );

const getScrollToIcon = useProps => {
  const typeId = useProps.itemsById.get(useProps.itemIds.first()).type;
  const type = useProps.typesById.get(typeId);

  if (type.canIncludeInNarrative || type.showInSequence || useProps.timelineShowTypes.includes(typeId)) {
    return (
      <Icon
        icon="sidebarScrollToDate"
        color="iconMono"
        onClick={() => onIdentifierClick(useProps.itemIds.first())}
        tooltip="Scroll to Item"
      />
    );
  }
  return null;
};

const getSelectedItemCount = useProps => {
  return (
    <div className="inspectorSelectedItemCount" onClick={() => onIdentifierClick(useProps.itemIds.first())}>
      {useProps.itemIds.size}
    </div>
  );
};

const onToggleChildren = (useProps, activeViewId, itemIid, value) => {
  const isMobile = preprocessorQuery.isMobile();
  if (isMobile && useProps.screenContext && useProps.screenContext.screenMode === screenModes.MobilePortrait) {
    useProps.toggleExpandSuperItemInMobileList(activeViewId, itemIid, value);
  } else {
    useProps.toggleExpandSuperItem(activeViewId, itemIid, value);
  }
};

const isExpanded = (useProps, itemId) => {
  const isMobile = preprocessorQuery.isMobile();
  const items =
    isMobile && useProps.screenContext && useProps.screenContext.screenMode === screenModes.MobilePortrait
      ? useProps.expandedItemsForMobile
      : useProps.expandedItems;
  return items.includes(itemId);
};

const renderComponents = useProps => {
  const {
    typesById,
    itemData,
    showCollapseLeft,
    onCollapseClick,
    collapseText,
    onLabelChange,
    onInitialsChange,
    onShortLabelChange,
    itemIds,
    typeOptions,
    colorOptions,
    onColorChange,
    superOptions,
    onSuperChange,
    typeData,
    activeViewId,
    isEditMode,
    onSetHasChildPopover
  } = useProps;

  const { displayId, swatchColor, type, color, label, initials, shortLabel } = itemData;
  const isAvatarType = typeSelectors.isAvatarType(typeData);
  const isShortLabelType = typeSelectors.isShortLabelType(typeData);
  const isIdentifierType = typeSelectors.isIdentifierType(typeData);
  const typeLabel = typeData.label;
  const typeLabelDisplay = typeSelectors.getTypeLabelDisplay(typeData);
  const itemLabel = label && label.length ? label : "";
  const isIos = preprocessorQuery.isIos();
  const isMobile = preprocessorQuery.isMobile();
  const showItemCount = itemIds.size > 1 && (!isIos || (isIos && !isEditMode));

  const showItemIdentifier = !isIos || (isIos && (!isEditMode || itemIds.size == 1));

  const identifierSlug = isIdentifierType ? (
    <IdentifierSlug label={displayId} color={swatchColor} onClick={() => onIdentifierClick(itemIds.first())} />
  ) : null;
  const scrollToIcon = !isAvatarType ? getScrollToIcon(useProps) : null;

  const headerContent = showItemCount ? (
    getSelectedItemCount(useProps)
  ) : showItemIdentifier ? (
    <div className="inspectorScrollToItem">
      {identifierSlug}
      {scrollToIcon}
    </div>
  ) : null;

  const collapseIcon = getCollapseIcon(onCollapseClick, showCollapseLeft, collapseText);

  const inspectorHeader = isMobile ? null : (
    <InspectorHeader>
      {showCollapseLeft ? collapseIcon : null}
      {headerContent}
      {showCollapseLeft ? null : collapseIcon}
    </InspectorHeader>
  );

  const typeDropdownStyle = isMobile ? null : { minWidth: "150px", maxWidth: "200px" };

  const placeholder = `${typeLabel} ${typeLabelDisplay}`;
  const labelRow = (
    <InspectorRow label={isAvatarType ? typeSelectors.getTypeLabelDisplay(typesById.get(type)) : ""}>
      <InspectorField>
        <FormTextField
          multiline={!isAvatarType && !typeData.showAsColumn}
          allowNewLine={false}
          autoFocusOnEmpty={isIos}
          value={itemLabel}
          onChange={label => onLabelChange(itemIds.toArray(), label)}
          placeholder={itemIds.size > 1 ? INSPECTOR_MULTIPLE_ITEM_SELECTED : placeholder}
          disabled={itemIds.size !== 1}
          autoExpandHeight={chooseValueForIos(true, false)}
        />
      </InspectorField>
    </InspectorRow>
  );

  const initialsRow = isAvatarType ? (
    <InspectorRow label="Initials">
      <InspectorField>
        <FormTextField
          multiline={false}
          value={initials}
          onChange={initials => onInitialsChange(itemIds.toArray(), initials)}
          disabled={!label || !label.length}
        />
      </InspectorField>
    </InspectorRow>
  ) : null;

  const shortLabelRow = isShortLabelType ? (
    <InspectorRow label="Short Label">
      <InspectorField>
        <FormTextField
          multiline={false}
          value={shortLabel}
          onChange={newLabel => onShortLabelChange(itemIds.toArray(), newLabel)}
          disabled={!label || !label.length}
        />
      </InspectorField>
    </InspectorRow>
  ) : null;

  const avatarRow = isAvatarType ? (
    <InspectorRow label="Avatar">
      <InspectorField style={{ marginTop: "10px" }}>
        <Identifier isAvatar={true} label={initials} color={swatchColor} />
      </InspectorField>
    </InspectorRow>
  ) : null;

  return (
    <InspectorGroup>
      {inspectorHeader}
      {labelRow}
      {initialsRow}
      {shortLabelRow}
      <InspectorRow label="Type">
        <InspectorField>
          <FormEnableDropdown
            options={typeOptions.toArray()}
            value={type}
            onChange={getOnTypeChange(useProps)}
            title="Type"
            backText={getLabelWithUntitled(itemLabel)}
            useParentWidth={false}
            style={typeDropdownStyle}
            onToggleMenu={onSetHasChildPopover}
            pulse={useProps.pulsateIconId === interfaceIdentifiers.INSPECTOR_TYPE}
          />
        </InspectorField>
      </InspectorRow>
      <InspectorRow label="Color">
        <InspectorField>
          <FormColorDropdown
            options={colorOptions.toArray()}
            moreColors={swatchColorOptions}
            value={color}
            onChange={color => onColorChange(itemIds.toArray(), color)}
            title="Color"
            backText={getLabelWithUntitled(itemLabel)}
            onToggleMenu={onSetHasChildPopover}
          />
        </InspectorField>
      </InspectorRow>
      <InspectorRow label="Parent">
        <InspectorField>
          <FormDropdown
            options={superOptions.toArray()}
            allowSearch={true}
            value={itemData.super}
            onChange={superId => onSuperChange(itemIds.toArray(), superId)}
            title="Parent"
            backText={getLabelWithUntitled(itemLabel)}
            onToggleMenu={onSetHasChildPopover}
          />
        </InspectorField>
        {isIos && itemData.hasChildren ? (
          <InspectorField>
            <FormCheckbox
              label="Expand Children"
              checkboxState={!itemIds.find(itemId => !isExpanded(useProps, itemId))}
              onChange={newState => onToggleChildren(useProps, activeViewId, itemData.id, newState)}
            />
          </InspectorField>
        ) : null}
      </InspectorRow>
      {avatarRow}
    </InspectorGroup>
  );
};

const InspectorTopBox = props => {
  return renderComponents(props);
};

InspectorTopBox.propTypes = {
  onCollapseClick: PropTypes.func,
  showCollapseLeft: PropTypes.bool,
  collapseText: PropTypes.string,

  // Incoming data from parent element
  itemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  itemData: PropTypeShapes.inspectorItem,
  items: PropTypeShapes.inspectorItemList,
  typeData: PropTypeShapes.denormalisedTypeDefinition,
  typesById: ImmutablePropTypes.map.isRequired,
  referencesById: ImmutablePropTypes.map.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  relationshipsById: ImmutablePropTypes.map.isRequired,
  // Options for dropdown lists
  typeOptions: PropTypeShapes.listOptionList,
  colorOptions: PropTypeShapes.listOptionList,
  superOptions: PropTypeShapes.listOptionList,
  // Dispatch actions
  onColorChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  onInitialsChange: PropTypes.func.isRequired,
  onShortLabelChange: PropTypes.func.isRequired,
  onSuperChange: PropTypes.func.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  timelineShowTypes: ImmutablePropTypes.list,
  isEditMode: PropTypes.bool,
  toggleExpandSuperItem: PropTypes.func,
  toggleExpandSuperItemInMobileList: PropTypes.func,
  expandedItems: ImmutablePropTypes.list,
  expandedItemsForMobile: ImmutablePropTypes.list,
  //#!if iOS === true
// onSetHasChildPopover: PropTypes.func,
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

const mapStateToProps = (state, props) => {
  const activeViewId = appStateSelectors.getActiveViewId(state.present);
  return {
    typeOptions: itemSelectors.getAllowedTypeOptionsByItems(state.present, props.itemIds),
    colorOptions: definitionSelectors.getColorOptions(state.present),
    superOptions: selectors.getSuperOptions(state.present, props.itemIds, props.typeData.id),
    typesById: typeSelectors.getTypesById(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    relationshipsById: relationshipSelectors.getRelationshipsById(state.present),
    activeViewId: activeViewId,
    expandedItems: appStateSelectors.getViewExpandedItems(state.present, activeViewId),
    timelineShowTypes: timelineViewSettingsSelectors.getTimelineViewSettings(state.present).showTypes,
    //#!if iOS === true
// isEditMode: uiStateSelectors.getIsEditMode(state.present),
    //#!endif
    //#!if mobile === true
// expandedItemsForMobile: appStateSelectors.getViewExpandedItemsForMobile(state.present)
    //#!endif
  };
};

const readonlyActions = {
  toggleExpandSuperItem: appStateOperations.toggleExpandSuperItem,
  //#!if mobile === true
// toggleExpandSuperItemInMobileList: appStateOperations.toggleExpandSuperItemInMobileList
  //#!endif
};

const editingActions = {
  onColorChange: (itemIds, color) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "color");
    return itemOperations.editColor(itemIds, color);
  },
  onLabelChange: (itemIds, label) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "label");
    return itemOperations.editLabel(itemIds, label);
  },
  onInitialsChange: (itemIds, initials) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "initials");
    return itemOperations.editInitials(itemIds, initials);
  },
  onShortLabelChange: (itemIds, newLabel) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "shortLabel");
    return itemOperations.editShortLabel(itemIds, newLabel);
  },
  onSuperChange: (itemIds, superId) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "super");
    return itemOperations.setSuper(itemIds, superId);
  },
  onTypeChange: (itemIds, typeId) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "type");
    return itemOperations.changeType(itemIds, typeId);
  },
  onSetHasChildPopover: uiStateOperations.setLeftPanelHasChildPopover
};

let exportedView = InspectorTopBox;
//#!if iOS === true
// exportedView = withScreenContext(InspectorTopBox);
//#!endif

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(exportedView);
